import axios from 'axios';
import { decodeJWT } from './jwt';
import { useAuthStore } from '@/store/global/auth';
import { storeToRefs } from 'pinia';

const hostname = window.location.hostname;
const environments = {
	development: ['localhost', '127.0.0.1'],
	staging: 'staging.semactic.com',
	testing: 'testing.semactic.com',
	production: 'django.semactic.com',
};

let baseURL = 'https://django.semactic.com/api/';

if (environments.development.includes(hostname)) {
	baseURL = `http://${hostname}:8000/api/`;
} else if (hostname.includes(environments.staging)) {
	baseURL = 'https://django-staging.semactic.com/api/';
} else if (hostname.includes(environments.testing)) {
	baseURL = 'https://django-testing.semactic.com/api/';
}

axios.defaults.baseURL = baseURL;

let isRefreshing = false;
let failedQueue = [];

// Anti-loop safeguard: if we've already redirected to /login in the last 5s,
// do not redirect again. Protects against any unexpected refresh-endpoint bug
// (e.g. backend cookie issue) that could otherwise bounce the user indefinitely.
const safeRedirectToLogin = async () => {
	const last = parseInt(sessionStorage.getItem('lastLoginRedirect') || '0', 10);
	if (Date.now() - last < 5000) {
		console.warn('[Axios Interceptor] safeRedirectToLogin: redirect skipped (anti-loop).');
		return;
	}
	sessionStorage.setItem('lastLoginRedirect', Date.now().toString());

	// Clear frontend auth state
	try {
		const authStore = useAuthStore();
		authStore.logout();
	} catch (e) {
		console.error('[Axios Interceptor] safeRedirectToLogin: authStore.logout() failed:', e);
	}

	// Sync backend logout (fire-and-forget with short timeout): invalidates the
	// httpOnly refresh cookie so we don't end up with a stale session at next
	// login attempt. Any failure is ignored — we MUST reach /login regardless.
	try {
		await axios.post('logout', {}, { withCredentials: true, timeout: 2000 });
	} catch (e) {
		// Silent: backend logout failure must not block redirection
	}

	window.location.replace('/login');
};

const processQueue = (error, token = null) => {
	failedQueue.forEach(prom => {
		if (error) {
			prom.reject(error);
		} else {
			prom.resolve(token);
		}
	});

	failedQueue = [];
};

const getProjectId = () => {
	const project = JSON.parse(localStorage.getItem('market'));
	if (!project) {
		return null;
	}
	return project.selectedProject;
};

const updateRole = role => {
	const user = JSON.parse(localStorage.getItem('auth'));

	if (user && user.role !== role) {
		user.role = role;
		user.access = getRoleName(role);
		localStorage.setItem('auth', JSON.stringify(user));
		location.reload();
	}
};

const getRoleName = role => {
	switch (role) {
		case 262144:
			return 'super_admin';
		case 1:
			return 'admin';
		case 8192:
			return 'project manager';
		case 32768:
			return 'digital specialist';
		case 65536:
			return 'digital enabler';
		case 2048:
			return 'partner';
		default:
			return 'guest';
	}
};

const sendRefresh = async () => {
	try {
		const response = await axios.post(
			'refresh',
			{ project_id: getProjectId() },
			{ withCredentials: true }
		);

		if (response.status === 200) {
			const { token, role } = response.data;
			const authStore = useAuthStore();
			
			authStore.setDecodedToken(token);
			authStore.updateRole(role);

			return token;
		}

		console.warn('[Axios Interceptor] sendRefresh: Refresh response status was not 200:', response.status);
		return null;
	} catch (error) {
		console.error('[Axios Interceptor] sendRefresh: Error during refresh request:', error);
		throw error;
	}
}

axios.interceptors.request.use(
	async (config) => {
		const unprotectedEndpoints = ['refresh', 'login', 'logout', 'google-oauth2', 'microsoft-oauth2'];
		if (unprotectedEndpoints.includes(config.url)) {
			return config;
		}

		const authStore = useAuthStore();
		const { decodedToken } = storeToRefs(authStore);

		if (decodedToken.value?.token) {
			config.headers['Authorization'] = `Bearer ${decodedToken.value.token}`;
			axios.defaults.headers.common['Authorization'] = `Bearer ${decodedToken.value.token}`;

			const currentTime = Math.floor(Date.now() / 1000);
			const tokenExp = decodedToken.value.exp;
			
			let expTimestamp = null;
			if (tokenExp !== undefined && tokenExp !== null) {
				if (typeof tokenExp === 'number') {
					expTimestamp = tokenExp;
				} else if (typeof tokenExp === 'string') {
					expTimestamp = parseInt(tokenExp, 10);
				}
			}
			
			const timeUntilExpiry = expTimestamp ? expTimestamp - currentTime : null;
			const isExpired = expTimestamp ? expTimestamp < currentTime : false;
			const isExpiringSoon = expTimestamp ? timeUntilExpiry > 0 && timeUntilExpiry <= 10 : false;
			
			if (expTimestamp && (isExpired || isExpiringSoon)) {
				if (isExpired) {
					console.warn('[Axios Interceptor] Request: Token expired', {
						exp: expTimestamp,
						current: currentTime,
						expiredBy: currentTime - expTimestamp,
						expiredBySeconds: currentTime - expTimestamp
					});
				} else {
					console.warn('[Axios Interceptor] Request: Token expiring soon, refreshing proactively', {
						exp: expTimestamp,
						current: currentTime,
						timeUntilExpiry: timeUntilExpiry
					});
				}
				
				if (isRefreshing) {
					return new Promise((resolve, reject) => {
						failedQueue.push({ 
							resolve: (token) => {
								if (token) {
									config.headers['Authorization'] = `Bearer ${token}`;
									axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
								}
								resolve(config);
							}, 
							reject: (error) => {
								console.error('[Axios Interceptor] Request: Queued request rejected:', error);
								reject(error);
							}
						});
					});
				}
				
				isRefreshing = true;
				try {
					const newToken = await sendRefresh();
					if (newToken) {
						config.headers['Authorization'] = `Bearer ${newToken}`;
						axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
						processQueue(null, newToken);
					} else {
						console.error('[Axios Interceptor] Request: Refresh failed - no token returned');
						processQueue(new Error('Failed to refresh token'), null);
					}
					isRefreshing = false;
				} catch (error) {
					console.error('[Axios Interceptor] Request: Error during refresh:', error);
					processQueue(error, null);
					isRefreshing = false;
				}
			}
		} else {
			// No token in store. Only attempt a proactive refresh on authenticated pages.
			// On public pages (login, createPassword, resetPassword) there is no session
			// by design — skip the refresh entirely and let the request go through as-is.
			const PUBLIC_PATHS = ['/createPassword', '/resetPassword', '/login'];
			const isPublicPage = PUBLIC_PATHS.some(p => window.location.pathname.startsWith(p));
			if (isPublicPage) {
				return config;
			}

			if (isRefreshing) {
				return new Promise((resolve, reject) => {
					failedQueue.push({ 
						resolve: (token) => {
							if (token) {
								config.headers['Authorization'] = `Bearer ${token}`;
								axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
							}
							resolve(config);
						}, 
						reject: (error) => {
							console.error('[Axios Interceptor] Request: Queued request (no token) rejected:', error);
							reject(error);
						}
					});
				});
			}
			
			isRefreshing = true;
			try {
				const newToken = await sendRefresh();
				if (newToken) {
					config.headers['Authorization'] = `Bearer ${newToken}`;
					axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
					processQueue(null, newToken);
				} else {
					console.error('[Axios Interceptor] Request: Refresh failed (no token) - no token returned');
					processQueue(new Error('Failed to refresh token'), null);
				}
			} catch (error) {
				console.error('[Axios Interceptor] Request: Error during refresh (no token):', error);
				processQueue(error, null);
			}
			isRefreshing = false;
		}

		return config;
	},
	(error) => {
		console.error('[Axios Interceptor] Request: Error during request setup:', error);
		return Promise.reject(error);
	}
);

axios.interceptors.response.use(
	response => response,
	async error => {
		const originalRequest = error.config;

		if (error.response && error.response.status === 401) {
			const detail = error.response?.data?.detail;

			if (detail === 'label not found') {
				return Promise.reject(error);
			}

			// If the refresh endpoint itself returns 401, the refresh cookie is no
			// longer valid — there is no recovery path. Redirect unconditionally
			// (previously gated on route meta.isAuth, which caused users to get
			// stuck on pages with no data instead of being redirected to login).
			if (error.response.config.url === 'refresh') {
				console.error('[Axios Interceptor] Response: Refresh endpoint returned 401 —', detail);
				processQueue(error, null);
				const PUBLIC_PATHS = ['/createPassword', '/resetPassword', '/login'];
				const isPublicPage = PUBLIC_PATHS.some(p => window.location.pathname.startsWith(p));
				if (!isPublicPage) {
					safeRedirectToLogin();
				}
				return Promise.reject(error);
			}

			if (!isRefreshing) {
				isRefreshing = true;
				return new Promise(function (resolve, reject) {
					axios.post('refresh', 
						{ project_id: getProjectId() },
						{ withCredentials: true }
					)
					.then(({ status, data }) => {
						if (status === 200) {
							const authStore = useAuthStore();
							authStore.setDecodedToken(data.token);
							authStore.updateRole(data.role);

							axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
							originalRequest.headers['Authorization'] = `Bearer ${data.token}`;

							processQueue(null, data.token);
							resolve(axios(originalRequest));
						} else {
							console.warn('[Axios Interceptor] Response: Refresh response status was not 200:', status);
						}
					})
					.catch(err => {
						console.error('[Axios Interceptor] Response: Error refreshing token from 401:', err);
						processQueue(err, null);
						reject(err);
					})
					.then(() => {
						isRefreshing = false;
					});
				});
			} else {
				return new Promise(function (resolve, reject) {
					failedQueue.push({ 
						resolve: (token) => {
							originalRequest.headers['Authorization'] = 'Bearer ' + token;
							resolve(axios(originalRequest));
						}, 
						reject: (err) => {
							console.error('[Axios Interceptor] Response: Queued request rejected:', err);
							reject(err);
						}
					});
				});
			}
		}

		return Promise.reject(error);
	},
);
